import {ClockIcon, CommentDiscussionIcon, XCircleFillIcon} from '@primer/octicons-react'
import type {Icon as PrimerIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {ForYouItem} from '../shared/ForYouItem'
import {ForYouLeadingIcon, type ForYouLeadingIconVariant} from '../shared/ForYouLeadingIcon'
import {useGoToMergeBox} from '../../mergebox/use-go-to-mergebox'
import {useMergeabilityData} from './use-mergeability-data'
import {decideReviewSubmittedItem, type ReviewSubmittedItemKind} from './shared/review-submitted-item-decider'
import {useUnresolvedThreadCount} from '../../../../stores/layout-selectors'
import {usePullRequestAnalytics} from '../../../../hooks/use-pull-request-analytics'

type ReviewSubmittedVariantConfig = {
  icon: PrimerIcon
  iconVariant: ForYouLeadingIconVariant
  emphasized: boolean
  title: string
  description: string
  mergePermitting: boolean
  itemName: string
}

const VARIANT_CONFIG: Record<Exclude<ReviewSubmittedItemKind, 'none'>, ReviewSubmittedVariantConfig> = {
  'required-changes-requested': {
    icon: XCircleFillIcon,
    iconVariant: 'danger',
    emphasized: true,
    title: 'Changes requested',
    description: 'Address the requested changes before merging.',
    mergePermitting: false,
    itemName: 'required-changes-requested',
  },
  'comments-resolution-required': {
    icon: XCircleFillIcon,
    iconVariant: 'danger',
    emphasized: true,
    title: 'Unresolved comments must be resolved',
    description: 'Resolve all comments before merging.',
    mergePermitting: false,
    itemName: 'comments-resolution-required',
  },
  'approval-required': {
    icon: ClockIcon,
    iconVariant: 'warning',
    emphasized: true,
    title: 'Approval required',
    description: 'Request another approving review before this can merge.',
    mergePermitting: false,
    itemName: 'approval-required',
  },
  'address-comments': {
    icon: CommentDiscussionIcon,
    iconVariant: 'warning',
    emphasized: false,
    title: 'Comments to address',
    description: 'Address the open review comments and ask for another review.',
    mergePermitting: true,
    itemName: 'address-comments',
  },
}

// Author-facing item that summarizes the state after a review has been submitted.
// Renders one of four mutually exclusive states, prioritized by the rubric:
// required-changes-requested > comments-resolution-required > approval-required > address-comments.
export function ReviewSubmitted({variant}: {variant?: 'default' | 'embedded'}) {
  const data = useMergeabilityData()
  const unresolvedThreadCount = useUnresolvedThreadCount()
  const goToMergeBox = useGoToMergeBox()
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  const decision = decideReviewSubmittedItem(data, unresolvedThreadCount > 0)
  if (decision.kind === 'none') return null

  const config = VARIANT_CONFIG[decision.kind]

  return (
    <ForYouItem
      leadingVisual={
        <ForYouLeadingIcon icon={config.icon} variant={config.iconVariant} emphasized={config.emphasized} />
      }
      emphasizedLeadingVisual={config.emphasized}
      title={config.title}
      description={config.description}
      itemName={config.itemName}
      variant={variant}
      actionSlot={
        config.mergePermitting ? (
          <Button
            size="small"
            variant="primary"
            onClick={event => {
              sendPullRequestAnalyticsEvent('overview.for_you_item_action', 'OVERVIEW_FOR_YOU_ITEM', {
                item_name: config.itemName,
                action_name: 'go_to_merge_box',
              })
              goToMergeBox(event.currentTarget)
            }}
          >
            Merge when ready
          </Button>
        ) : (
          <Button size="small" variant="default" inactive>
            Merge when ready
          </Button>
        )
      }
    />
  )
}
